import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    makeStyles,
    MenuItem,
    Paper,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    FilterChipItem,
} from './CatalogFilterChrome'
import { toast } from 'react-toastify'
import { DropFiles } from '../../components/DropFiles/DropFiles'
import { DocumentsTable } from './DocumentsTable'
import { DocumentViewModal } from './DocumentViewModal'
import { DocumentEditModal } from './DocumentEditModal'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import {
    DocumentUploadChoice,
    DocumentUploadKindDialog,
} from './DocumentUploadKindDialog'
import { DocumentShareModal } from './DocumentShareModal'
import { useCompanyDocuments } from '../../hooks/useCompanyDocuments'
import { useOrganizationDocumentTypeCoverage } from '../../hooks/useOrganizationDocumentTypeCoverage'
import { useOrganizationDocumentTypes } from '../../hooks/useOrganizationDocumentTypes'
import { promoteCompanyDocumentToSignableDoc } from '../../hooks/Outreach/useSignableDocs'
import { useUser } from '../../hooks/useUser'
import { CompanyDocument } from '../../models/Company/types'
import { UpdateCompanyDocumentInput } from '../../hooks/useCompanyDocuments'
import { Routes } from '../../navigation/config/routes'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    searchField: {
        minWidth: 220,
        flex: '1 1 200px',
        maxWidth: 320,
    },
    filterField: {
        minWidth: 160,
    },
    requiredPanel: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    requiredPanelMissing: {
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        backgroundColor: '#fff7ed',
    },
    requiredPanelComplete: {
        borderLeft: `4px solid ${theme.palette.success.main}`,
        backgroundColor: '#f0fdf4',
    },
    requiredPanelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    requiredTitle: {
        fontWeight: 600,
    },
    missingTypeList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
}))

const allowedFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx',
    ],
    'text/plain': ['.txt'],
    'text/markdown': ['.md'],
    'text/html': ['.html', '.htm'],
}

export const Documents: React.FC = () => {
    const classes = useStyles()
    const history = useHistory()
    const {
        rootUser,
        workspaceUser,
        organization: userOrganization,
    } = useUser()
    const [promotingIds, setPromotingIds] = useState<Set<number>>(new Set())

    const organizationId =
        workspaceUser?.active_workspace?.organization ??
        userOrganization ??
        null
    const activePropertyId = workspaceUser?.active_workspace?.id ?? null

    const [libraryFilterKey, setLibraryFilterKey] = useState<string>('all')
    const [propertyFilterId, setPropertyFilterId] = useState<number | null>(
        activePropertyId,
    )

    useEffect(() => {
        if (activePropertyId != null) {
            setPropertyFilterId(activePropertyId)
        }
    }, [activePropertyId])

    const propertyOptions = useMemo(() => {
        if (organizationId == null || !rootUser?.workspaces?.length) {
            return []
        }
        const seen = new Set<number>()
        const options: { id: number; name: string }[] = []
        for (const ws of rootUser.workspaces) {
            const company = ws.active_workspace
            if (
                company.organization !== organizationId ||
                company.company_type !== 'APARTMENT' ||
                seen.has(company.id)
            ) {
                continue
            }
            seen.add(company.id)
            options.push({ id: company.id, name: company.name })
        }
        return options.sort((a, b) => a.name.localeCompare(b.name))
    }, [organizationId, rootUser?.workspaces])

    const selectedPropertyName =
        propertyOptions.find((p) => p.id === propertyFilterId)?.name ?? null

    const listParams = useMemo(
        () => ({
            organization: organizationId,
            generalOnly: libraryFilterKey === 'general',
            documentTypeId:
                libraryFilterKey !== 'all' && libraryFilterKey !== 'general'
                    ? Number(libraryFilterKey)
                    : null,
            companyId: propertyFilterId,
        }),
        [organizationId, libraryFilterKey, propertyFilterId],
    )

    const {
        documents,
        isLoadingDocuments,
        uploadDocuments,
        processDocument,
        deleteDocument,
        updateDocument,
        isUploading,
        isDeleting,
        isUpdating,
        processingIds,
        refetchDocuments,
    } = useCompanyDocuments(listParams)

    const {
        documentTypes: organizationDocumentTypes,
    } = useOrganizationDocumentTypes({
        organization: organizationId,
        includeInactive: false,
    })

    const {
        coverage: documentTypeCoverage,
        isLoading: isLoadingCoverage,
    } = useOrganizationDocumentTypeCoverage({
        organization: organizationId,
        includeInactive: false,
        enabled: organizationId != null && activePropertyId != null,
    })

    const requiredDocumentStatus = useMemo(
        () =>
            documentTypeCoverage
                .map((group) => {
                    const propertyCoverage = group.properties.find(
                        (property) => property.id === activePropertyId,
                    )
                    return {
                        documentType: group.document_type,
                        isRequired: propertyCoverage?.is_required ?? false,
                        hasDocument: propertyCoverage?.has_document ?? false,
                    }
                })
                .filter((status) => status.isRequired),
        [activePropertyId, documentTypeCoverage],
    )

    const missingRequiredDocumentTypes = useMemo(
        () => requiredDocumentStatus.filter((status) => !status.hasDocument),
        [requiredDocumentStatus],
    )

    const [uploadKindDialogOpen, setUploadKindDialogOpen] = useState(false)
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [searchText, setSearchText] = useState('')
    const [showCompleteRequired, setShowCompleteRequired] = useState(false)
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [
        selectedDocument,
        setSelectedDocument,
    ] = useState<CompanyDocument | null>(null)

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [
        documentToDelete,
        setDocumentToDelete,
    ] = useState<CompanyDocument | null>(null)

    const [shareModalOpen, setShareModalOpen] = useState(false)
    const [
        documentToShare,
        setDocumentToShare,
    ] = useState<CompanyDocument | null>(null)

    const [editModalOpen, setEditModalOpen] = useState(false)
    const [
        documentToEdit,
        setDocumentToEdit,
    ] = useState<CompanyDocument | null>(null)

    useEffect(() => {
        setPage(0)
    }, [libraryFilterKey, propertyFilterId])

    const filteredDocuments = useMemo(() => {
        if (!searchText.trim()) {
            return documents
        }
        const searchLower = searchText.toLowerCase()
        return documents.filter((doc) => {
            const typeName = doc.document_type_detail?.name
            return (
                doc.name.toLowerCase().includes(searchLower) ||
                (doc.ai_summary || '').toLowerCase().includes(searchLower) ||
                (typeName || '').toLowerCase().includes(searchLower)
            )
        })
    }, [documents, searchText])

    const hasActiveLibraryFilters =
        searchText.trim() !== '' || libraryFilterKey !== 'all'

    const clearDrawerFilters = () => {
        setLibraryFilterKey('all')
        setPage(0)
    }

    const libraryFilterChips = useMemo<FilterChipItem[]>(() => {
        const chips: FilterChipItem[] = []
        if (libraryFilterKey === 'general') {
            chips.push({
                id: 'type',
                label: 'Type: General only',
                onDelete: () => setLibraryFilterKey('all'),
            })
        } else if (libraryFilterKey !== 'all') {
            const typeName =
                organizationDocumentTypes.find(
                    (type) => String(type.id) === libraryFilterKey,
                )?.name || 'Type'
            chips.push({
                id: 'type',
                label: `Type: ${typeName}`,
                onDelete: () => setLibraryFilterKey('all'),
            })
        }
        return chips
    }, [libraryFilterKey, organizationDocumentTypes])

    const paginatedDocuments = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredDocuments.slice(start, end)
    }, [filteredDocuments, page, rowsPerPage])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const runUpload = useCallback(
        async (files: File[], documentTypeId?: number) => {
            const results = await uploadDocuments(
                files,
                undefined,
                documentTypeId,
            )

            results.forEach((result) => {
                if (result.status === 'fulfilled') {
                    const doc = result.value.data
                    processDocument.mutate(doc.id)
                }
            })
        },
        [processDocument, uploadDocuments],
    )

    const handleFilesDropped = (files: File[]) => {
        if (files.length === 0) return
        if (organizationId != null) {
            setPendingFiles(files)
            setUploadKindDialogOpen(true)
        } else {
            void runUpload(files, undefined)
        }
    }

    const handleCloseUploadKindDialog = () => {
        setUploadKindDialogOpen(false)
        setPendingFiles([])
    }

    const handleDocumentUploadKindChosen = (choice: DocumentUploadChoice) => {
        const documentTypeId =
            choice.kind === 'general' ? undefined : choice.documentTypeId
        void runUpload(pendingFiles, documentTypeId)
        setUploadKindDialogOpen(false)
        setPendingFiles([])
    }

    const handleView = (doc: CompanyDocument) => {
        setSelectedDocument(doc)
        setViewModalOpen(true)
    }

    const handleCloseViewModal = () => {
        setViewModalOpen(false)
        setSelectedDocument(null)
    }

    const handleDeleteClick = (id: number) => {
        const doc = documents.find((d) => d.id === id)
        if (doc) {
            setDocumentToDelete(doc)
            setDeleteDialogOpen(true)
        }
    }

    const handleConfirmDelete = () => {
        if (documentToDelete) {
            deleteDocument.mutate(documentToDelete.id, {
                onSuccess: () => {
                    setDeleteDialogOpen(false)
                    setDocumentToDelete(null)
                },
            })
        }
    }

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false)
        setDocumentToDelete(null)
    }

    const handleShare = (doc: CompanyDocument) => {
        setDocumentToShare(doc)
        setShareModalOpen(true)
    }

    const handleCloseShareModal = () => {
        setShareModalOpen(false)
        setDocumentToShare(null)
    }

    const handleEdit = (doc: CompanyDocument) => {
        setDocumentToEdit(doc)
        setEditModalOpen(true)
    }

    const handleCloseEditModal = () => {
        setEditModalOpen(false)
        setDocumentToEdit(null)
    }

    const handleSaveEdit = (
        documentId: number,
        data: UpdateCompanyDocumentInput,
    ) => {
        updateDocument.mutate(
            { documentId, data },
            {
                onSuccess: (updated) => {
                    if (selectedDocument?.id === documentId) {
                        setSelectedDocument(updated.data)
                    }
                    handleCloseEditModal()
                },
            },
        )
    }

    const handleMakeSignable = async (doc: CompanyDocument) => {
        if (
            activePropertyId != null &&
            propertyFilterId != null &&
            propertyFilterId !== activePropertyId
        ) {
            toast.error(
                'Switch to this property’s workspace to prepare documents for signing.',
            )
            return
        }
        if (doc.signable_doc?.id) {
            history.push(
                Routes.manage.signableDocs.detail.replace(
                    ':id',
                    String(doc.signable_doc.id),
                ),
                { continueSetup: doc.signable_doc.status === 'DRAFT' },
            )
            return
        }
        setPromotingIds((prev) => new Set(prev).add(doc.id))
        try {
            const signableDoc = await promoteCompanyDocumentToSignableDoc({
                company_document_id: doc.id,
                name: doc.name,
            })
            await refetchDocuments()
            history.push(
                Routes.manage.signableDocs.detail.replace(
                    ':id',
                    String(signableDoc.id),
                ),
                { continueSetup: true },
            )
        } catch (error: unknown) {
            const message =
                (error as { response?: { data?: { error?: string } } })
                    ?.response?.data?.error ??
                'Could not prepare this document for signing.'
            toast.error(message)
        } finally {
            setPromotingIds((prev) => {
                const next = new Set(prev)
                next.delete(doc.id)
                return next
            })
        }
    }

    const handleReanalyze = (id: number) => {
        processDocument.mutate(id)
    }

    if (isLoadingDocuments) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    style={{ gap: 8 }}
                >
                    <Typography
                        variant="h5"
                        component="h2"
                        className={classes.title}
                    >
                        Documents
                    </Typography>
                </Box>
            </Box>

            {organizationId != null &&
                activePropertyId != null &&
                (isLoadingCoverage || requiredDocumentStatus.length > 0) && (
                    <Paper
                        elevation={0}
                        className={`${classes.requiredPanel} ${
                            missingRequiredDocumentTypes.length > 0
                                ? classes.requiredPanelMissing
                                : classes.requiredPanelComplete
                        }`}
                    >
                        <Box className={classes.requiredPanelHeader}>
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    className={classes.requiredTitle}
                                >
                                    Required documents
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Track required document types for this
                                    property.
                                </Typography>
                            </Box>
                            {isLoadingCoverage ? (
                                <Box display="flex" alignItems="center">
                                    <CircularProgress size={18} />
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ marginLeft: 8 }}
                                    >
                                        Checking...
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 8 }}
                                >
                                    {requiredDocumentStatus.length >
                                        missingRequiredDocumentTypes.length && (
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                setShowCompleteRequired(
                                                    (open) => !open,
                                                )
                                            }
                                        >
                                            {showCompleteRequired
                                                ? 'Hide on file'
                                                : 'Show on file'}
                                        </Button>
                                    )}
                                    <Chip
                                        size="small"
                                        color={
                                            missingRequiredDocumentTypes.length >
                                            0
                                                ? 'secondary'
                                                : 'primary'
                                        }
                                        label={
                                            missingRequiredDocumentTypes.length >
                                            0
                                                ? `${missingRequiredDocumentTypes.length} missing`
                                                : 'Complete'
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                        {!isLoadingCoverage &&
                            (missingRequiredDocumentTypes.length > 0 ||
                            showCompleteRequired ? (
                                <Box className={classes.missingTypeList}>
                                    {(showCompleteRequired
                                        ? requiredDocumentStatus
                                        : missingRequiredDocumentTypes
                                    ).map(({ documentType, hasDocument }) => (
                                        <Chip
                                            key={documentType.id}
                                            label={
                                                showCompleteRequired
                                                    ? `${documentType.name}${
                                                          hasDocument
                                                              ? ' · on file'
                                                              : ' · missing'
                                                      }`
                                                    : documentType.name
                                            }
                                            size="small"
                                            color={
                                                hasDocument
                                                    ? 'default'
                                                    : 'secondary'
                                            }
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    This property has at least one document for
                                    every required document type.
                                </Typography>
                            ))}
                    </Paper>
                )}

            <DropFiles
                onDrop={handleFilesDropped}
                displayString={
                    isUploading
                        ? 'Uploading...'
                        : 'Drag and drop files here (PDF, DOCX, TXT, MD, HTML)'
                }
                fileTypes={allowedFileTypes}
                multipleFiles={true}
            />

            {organizationId != null && (
                <DocumentUploadKindDialog
                    open={uploadKindDialogOpen}
                    onClose={handleCloseUploadKindDialog}
                    files={pendingFiles}
                    documentTypes={organizationDocumentTypes}
                    onChoose={handleDocumentUploadKindChosen}
                />
            )}

            <Box
                display="flex"
                alignItems="flex-start"
                style={{ gap: 12, flexWrap: 'wrap' }}
            >
                <Box style={{ flex: 1, minWidth: 280 }}>
                    <CatalogFilterBar
                        searchPlaceholder="Search documents..."
                        searchValue={searchText}
                        onSearchChange={(value) => {
                            setSearchText(value)
                            setPage(0)
                        }}
                        onOpenFilters={() => setFilterDrawerOpen(true)}
                        chips={libraryFilterChips}
                        onClearAll={
                            libraryFilterChips.length > 0
                                ? clearDrawerFilters
                                : undefined
                        }
                        resultLabel={`${filteredDocuments.length} document${
                            filteredDocuments.length !== 1 ? 's' : ''
                        }`}
                    />
                </Box>
                <TextField
                    className={classes.filterField}
                    label="Property"
                    variant="outlined"
                    size="small"
                    select
                    value={propertyFilterId ?? ''}
                    onChange={(e) =>
                        setPropertyFilterId(Number(e.target.value))
                    }
                >
                    {propertyOptions.map((property) => (
                        <MenuItem key={property.id} value={property.id}>
                            {property.name}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
            <CatalogFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                title="Document filters"
                activeCount={libraryFilterChips.length}
                onClear={clearDrawerFilters}
            >
                <TextField
                    label="Document type"
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={libraryFilterKey}
                    onChange={(e) =>
                        setLibraryFilterKey(e.target.value as string)
                    }
                >
                    <MenuItem value="all">All documents</MenuItem>
                    <MenuItem value="general">General only</MenuItem>
                    {organizationDocumentTypes
                        .filter((t) => t.is_active)
                        .map((t) => (
                            <MenuItem key={t.id} value={String(t.id)}>
                                {t.name}
                            </MenuItem>
                        ))}
                </TextField>
            </CatalogFilterDrawer>

            <DocumentsTable
                documents={paginatedDocuments}
                onDelete={handleDeleteClick}
                onView={handleView}
                onEdit={handleEdit}
                onShare={handleShare}
                onReanalyze={handleReanalyze}
                onMakeSignable={handleMakeSignable}
                promotingIds={promotingIds}
                processingIds={processingIds}
                totalCount={filteredDocuments.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <DocumentViewModal
                isOpen={viewModalOpen}
                onClose={handleCloseViewModal}
                document={selectedDocument}
            />

            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                documentName={documentToDelete?.name || ''}
                isDeleting={isDeleting}
            />

            <DocumentShareModal
                open={shareModalOpen}
                onClose={handleCloseShareModal}
                document={documentToShare}
                apartmentId={propertyFilterId}
                apartmentName={selectedPropertyName}
                activePropertyId={activePropertyId}
            />

            <DocumentEditModal
                isOpen={editModalOpen}
                onClose={handleCloseEditModal}
                document={documentToEdit}
                documentTypes={organizationDocumentTypes}
                onSave={handleSaveEdit}
                isSaving={isUpdating}
            />
        </Box>
    )
}
